/* eslint-disable no-undef */
/* eslint-disable default-case */
import React from "react";
import { Controller } from "react-hook-form";
import { Col, Form, FormControl, Row } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { getMaxDob, getSubdomainPath, normalizeMonthYear, renderGlobalModal, validateDob } from "../../../../utils/helpers";
import { buildValidationRules } from "./updateProfileDataHelpers";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { deleteResume } from "../../Slice/updateProfileSliceNew";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaRegCalendarAlt } from "react-icons/fa";

const renderLinkedInField = (field, deps) => {
  const { control, errors } = deps;
  const { placeholder, validation, is_required, status } = field;
  const validationRule = validation;
  const subDomainPath = getSubdomainPath();

  return status ? (
    <Col md={12} className="mb-3">
      <div
        className="form-group position-relative"
        style={{ marginBottom: "0px" }}
      >
        <Form.Label>{field.label}</Form.Label>
        <div className="form-label overflow-hidden ">
          <span>
            <i className="fab fa-linkedin-in"></i>
          </span>

          <Controller
            name="linkedin"
            control={control}
            defaultValue=""
            rules={{
              ...buildValidationRules(validationRule, is_required),
            }}
            render={({ field }) => (
              <Form.Control
                type="text"
                placeholder={placeholder}
                className="form-control"
                {...field}
              />
            )}

          />
        </div>
        {(subDomainPath !== 'siddev' && subDomainPath !== 'siduat' && subDomainPath !== 'sid') &&
          `If you are registering as both a mentor and a mentee, please add a
          single or double slash ("/" or "//") at the end of your LinkedIn link.`
        }
      </div>

      {errors?.linkedin && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors.linkedin.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramStudyOtherField = (field, deps) => {
  const { register, errors, watch } = deps;
  const { reg_key, field_type, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status && watch("program_study_id") === "0" ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      <Form.Group controlId="formBasicEmail" className="">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderGenericTextField = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, field_type, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const isNameField = ["first_name", "last_name"].includes(reg_key);

  let columnClass = "";
  if (reg_key === "first_name") {
    columnClass = "pe-sm-2";
  } else if (reg_key === "last_name") {
    columnClass = "ps-sm-2";
  }

  return status ? (
    <Col
      md={isNameField ? 6 : 12}
      sm={isNameField ? 6 : 12}
      className={`mb-3 ${columnClass}`}
    >
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicEmail" className="">
        {(reg_key === "restrictions_boundaries" ||
          reg_key === "communication_tools" ||
          reg_key === "frequency_communication" ||
          reg_key === "topics") && <></>}
        <Form.Control
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderTextField = (field, deps) => {
  const { reg_key } = field;

  if (reg_key === "linkedin") {
    return renderLinkedInField(field, deps);
  }

  if (reg_key === "program_study_other") {
    return renderProgramStudyOtherField(field, deps);
  }

  if (reg_key === "past_board_roles") {
    return renderPastBoardRolesField(field, deps);
  }

  // Board roles repeater fields - skip individual rendering, they're rendered as a group
  const boardRoleFieldKeys = ['rep_company_name', 'rep_board_title', 'rep_start_date', 'rep_end_date'];
  if (boardRoleFieldKeys.includes(reg_key)) {
    return null; // These fields are rendered as a group by renderBoardRolesInline
  }

  return renderGenericTextField(field, deps);
};

export const renderEmailField = (field, deps) => {

  const { register, errors, email, setEmail, readOnly, setValue, clearErrors, settingData } = deps;
  const { reg_key, field_type, placeholder, validation, is_required } = field;
  const validationRule = validation;

  return (
    <Col md={12} sm={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicEmail">
        <FormControl
          type={field_type}
          placeholder={placeholder}
          name={reg_key}
          value={email}
          disabled={settingData?.data?.is_sso_enable == "1"}
          readOnly={readOnly}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          onChange={(e) => {
            setValue("email", e.target.value);
            setEmail(e?.target?.value);
            clearErrors("email");
          }}
        />
        {errors?.email && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.email?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

export const renderPasswordField = (field, deps) => {
  // Password field is not rendered in update profile
  return null;
};



const renderProgramSelect = (field, deps) => {
  const { control, programmes, setValue } = deps;
  const { reg_key, placeholder, status } = field;
  const defaultProgramme = programmes?.[0]?.id ?? "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Controller
        name={reg_key}
        control={control}
        rules={{}}
        defaultValue={defaultProgramme}
        render={({ field: controllerField }) => {
          const currentValue = controllerField.value !== undefined && controllerField.value !== ""
            ? controllerField.value
            : defaultProgramme;

          // Sync into RHF state so the value appears in the submit payload.
          // This is needed because disabled fields are excluded from native form
          // submission, and Controller's defaultValue alone is not enough when
          // superUserParams doesn't pre-populate this key.
          // Use both conditions to ensure proper sync when programmes data loads
          if (programmes && programmes.length > 0 && (!controllerField.value || controllerField.value === "")) {
            // Immediately sync the default value into form state
            setValue(reg_key, defaultProgramme, { shouldValidate: false });
          } else if (currentValue !== "" && controllerField.value !== currentValue) {
            // Also handle case where value needs updating
            setTimeout(() => setValue(reg_key, currentValue, { shouldValidate: false }), 0);
          }

          return (
            <select
              value={currentValue}
              disabled
              className="form-control"
            >
              <option value="">{placeholder}</option>
              {programmes?.map((value) => (
                <option key={value.id} value={value.id}>
                  {value.program}
                </option>
              ))}
            </select>
          );
        }}
      />
    </Col>
  ) : null;
};

const renderProgramStudySelect = (field, deps) => {
  const { register, errors, setValue, trigger, clearErrors, programStudyId, handleProgrammesChange } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <select
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={`form-control ${errors?.[reg_key] ? "is-invalid" : ""}`}
        onChange={(e) => {
          const value = e.target.value;
          handleProgrammesChange(value);
          setValue(reg_key, value);
          if (value) {
            clearErrors(reg_key);
          } else {
            trigger(reg_key);
          }
        }}
      >
        <option value="">{placeholder}</option>
        {programStudyId?.map((value) => (
          <option key={value.id} value={value.id}>
            {value.title}
          </option>
        ))}
        <option value={"0"}>Others</option>
      </select>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
            display: "block",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderUniversityPartnerSelect = (field, deps) => {
  const { register, errors, trigger, clearErrors, university_partners_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicPassword">
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          className={"form-control"}
          onChange={(e) => {
            clearErrors(reg_key);
            if (!e.target.value && is_required) {
              trigger(reg_key);
            }
          }}
        >
          <option value="">{placeholder}</option>
          {university_partners_list?.map((item) => (
            <option value={item.id} key={item.id}>
              {item.title}
            </option>
          ))}
        </Form.Control>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderYearSelect = (field, deps) => {
  const { register, errors, years } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formCurrentCourse">
        <Form.Control
          as="select"
          name={reg_key}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          className={"form-control"}
        >
          <option value="">{placeholder}</option>
          {years?.map((year) => (
            <option value={year} key={year}>
              {year}
            </option>
          ))}
        </Form.Control>
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderExperienceSelect = (field, deps) => {
  const { errors, setValue, userExperience, experience, setExperience, control } = deps;
  const { reg_key, label, placeholder, is_required, status } = field;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Group controlId="formBasicExperience">
        <Controller
          name={reg_key}
          control={control}
          rules={{
            required: is_required ? `${label} is required` : false,
          }}
          render={({ field }) => (
            <>
              <Form.Control
                as="select"
                {...field}
                value={
                  field.value ||
                  (experience?.length > 0
                    ? experience[0]
                    : "")
                }
                className={`form-control mb-3 ${errors?.[reg_key] ? "is-invalid" : ""}`}
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  setExperience(selectedValue ? [selectedValue] : []);
                  field.onChange(selectedValue);
                  setValue(reg_key, selectedValue, { shouldValidate: true });
                }}
              >
                <option value="">{placeholder}</option>
                {userExperience?.map((val) => (
                  <option key={val?.id} value={val?.id}>
                    {val?.title}
                  </option>
                ))}
              </Form.Control>

              {errors?.[reg_key] && (
                <span
                  role="alert"
                  style={{
                    color: "#dc3545",
                    fontSize: "0.875em",
                    margin: "4px 0px 8px",
                  }}
                >
                  {errors?.[reg_key]?.message}
                </span>
              )}
            </>
          )}
        />
      </Form.Group>
    </Col>
  ) : null;
};

const renderCountrySelect = (field, deps) => {
  const { control, errors, clearErrors, country, country1, userData, setCountry } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const handleCountryChange = (value, field) => {
    setCountry(value);
    field.onChange(value);
    if (value) {
      clearErrors(reg_key);
    }
  };

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Controller
        name={reg_key}
        control={control}
        rules={{
          ...buildValidationRules(validationRule, is_required),
        }}
        render={({ field, fieldState: { error } }) => (
          <>
            <Form.Control
              as="select"
              {...field}
              value={field.value ?? country1 ?? userData?.country ?? ""}

              className="mb-1"
              onChange={(e) => handleCountryChange(e.target.value, field)}

            >
              <option value="">{placeholder}</option>
              {country?.map((item) => (
                <option value={item.label} key={item.label}>
                  {item.label}
                </option>
              ))}
            </Form.Control>
            {errors?.[reg_key] && (
              <span
                role="alert"
                style={{
                  color: "#dc3545",
                  fontSize: "0.875em",
                  margin: "4px 0px 8px",
                }}
              >
                {errors?.[reg_key]?.message}
              </span>
            )}
          </>
        )}
      />
    </Col>
  ) : null;
};

const renderLanguageSelect = (field, deps) => {
  const { control, errors, languageOptions, customStyles } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group controlId="formBasicLanguage" className="react-select-menu-option">
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name="language"
          control={control}
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => (
            <Select
              {...field}
              isMulti
              options={languageOptions}
              placeholder={placeholder}
              className="basic-multi-select language_select"
              classNamePrefix="select"
              onChange={(selectedOptions) => {
                const values = selectedOptions?.map((opt) => opt.value) || [];
                field.onChange(values);
              }}
              value={languageOptions.filter((option) =>
                field.value?.includes(option.value)
              )}
              styles={customStyles}
            />
          )}
        />

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderIndustrySelect = (field, deps) => {
  const { register, errors, setIndustryData, industryData, industry_list } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        defaultValue={industryData}
        className="mb-1"
        name={reg_key}
        onChange={(e) => {
          setIndustryData(e?.target?.value);
        }}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
      >
        <option value="">{placeholder}</option>
        {industry_list.map((item) => (
          <option value={item.id} key={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderMenteeCountSelect = (field, deps) => {
  const { register, errors } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        <option value="1">1</option>
        <option value="2">2</option>
        <option value="3">3</option>
        <option value="4">4</option>
        <option value="5">5</option>
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderProgramInterestSelect = (field, deps) => {
  const { register, errors, particularProgram } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md="12" className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className={"form-control"}
      >
        <option value="">{placeholder}</option>
        {particularProgram?.map((res, i) => {
          return (
            <option key={res?.id} value={res?.id}>
              {`${res?.title}`}
            </option>
          );
        })}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

// Board-related field renderers
const renderYearsOfBoardExperienceSelect = (field, deps) => {
  const { register, errors, watch, boardExperience } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;
  const currentValue = watch(reg_key) || "";

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Label>{field.label}</Form.Label>
      <Form.Control
        as="select"
        name={reg_key}
        value={currentValue}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
        className="form-control"
      >
        <option value="">{placeholder}</option>
        {boardExperience?.map((item) => (
          <option key={item.id} value={item.id}>
            {item.title}
          </option>
        ))}
      </Form.Control>
      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  ) : null;
};

const renderAccreditedDirectorSelect = (field, deps) => {
  const { control, errors, accreditedDirector, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = accreditedDirector?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={null}
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            const selectedValue = options.find(
              (option) => Number.parseInt(option.value) === Number.parseInt(field.value)
            );
            return (
              <Select
                {...field}
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOption) => {
                  const value = selectedOption
                    ? Number.parseInt(selectedOption.value)
                    : null;

                  field.onChange(value);
                  setValue(reg_key, value);
                }}
                value={selectedValue || null}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

const renderBoardExperienceLevelSelect = (field, deps) => {
  const { control, errors, boardExperienceLevel, customStyles, setValue } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  const options = boardExperienceLevel?.map(item => ({
    value: item.id,
    label: item.title
  })) || [];

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group className="react-select-menu-option">
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]}
          rules={{
            ...buildValidationRules(validationRule, is_required),
          }}
          render={({ field }) => {
            const fieldValue = Array.isArray(field.value) ? field.value : [];
            const normalizedFieldValue = new Set(
              fieldValue.map(val => Number.parseInt(val))
            );

            return (
              <Select
                {...field}
                isMulti
                options={options}
                placeholder={placeholder}
                className="basic-multi-select"
                classNamePrefix="select"
                onChange={(selectedOptions) => {
                  const selectedValues = selectedOptions
                    ? selectedOptions.map((opt) => Number.parseInt(opt.value))
                    : [];
                  field.onChange(selectedValues);
                  setValue(reg_key, selectedValues);
                }}
                value={options.filter((option) =>
                  normalizedFieldValue.has(Number.parseInt(option.value))
                )}
                styles={customStyles}
              />
            );
          }}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

// Board Roles Repeater Component - handles dynamic groups of board role entries
// This component is used for rep_company_name, rep_board_title, rep_start_date, rep_end_date fields
const BoardRolesRepeater = ({ allFields, deps }) => {
  const { watch, setValue, trigger, register, setting, formState } = deps;

  // Get the individual field definitions
  const pastBoardRolesField = allFields?.find(f => f.reg_key === 'past_board_roles');
  const companyNameField = allFields?.find(f => f.reg_key === 'rep_company_name');
  const boardTitleField = allFields?.find(f => f.reg_key === 'rep_board_title');
  const startDateField = allFields?.find(f => f.reg_key === 'rep_start_date');
  const endDateField = allFields?.find(f => f.reg_key === 'rep_end_date');

  // Parse board_roles_validation from Redux settings
  // Extract min and max values, ignoring the type field
  // Example: [{"key":"min","type":"Mentor","value":1,"message":"At least 2 board roles required"},{"key":"max","type":"Mentees","value":3}]
  // We only care about key, value, and message - ignore type completely
  let minRoles = 0;
  let maxRoles = 1;
  let minValidationMessage = "";

  if (setting?.board_roles_validation) {
    try {
      // Parse JSON string if needed
      const validationData = typeof setting.board_roles_validation === 'string'
        ? JSON.parse(setting.board_roles_validation)
        : setting.board_roles_validation;

      // Find min and max values - ignore the type field completely
      if (Array.isArray(validationData)) {
        // Find the min entry (ignore type)
        const minObj = validationData.find(v => v.key === 'min');
        // Find the max entry (ignore type)
        const maxObj = validationData.find(v => v.key === 'max');

        minRoles = minObj?.value || 0;
        maxRoles = maxObj?.value || 1;
        minValidationMessage = minObj?.message || "";
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  // Check if ALL board role fields have status = 1 (enabled)
  // If any field has status = 0, don't show the entire board roles section
  const allFieldsEnabled = companyNameField?.status === 1 &&
    boardTitleField?.status === 1 &&
    startDateField?.status === 1 &&
    endDateField?.status === 1;

  // Show the repeater ONLY if:
  // 1. All 4 fields exist (fields are defined in registration settings)
  // 2. All 4 fields have status=1 (fields are enabled)
  // 3. maxRoles > 0 (board roles are allowed based on validation settings)
  const shouldShowRepeater = companyNameField && boardTitleField && startDateField && endDateField && allFieldsEnabled && maxRoles > 0;

  // Watch the board_roles array to track current count
  let boardRolesArray = watch('board_roles') || [];

  if (typeof boardRolesArray === "string") {
    try {
      boardRolesArray = JSON.parse(boardRolesArray);
    } catch {
      boardRolesArray = [];
    }
  }

  if (!Array.isArray(boardRolesArray)) {
    boardRolesArray = [];
  }

  // Initialize minimum required groups IMMEDIATELY if needed
  // Don't wait for useEffect - do it synchronously on first render
  if (shouldShowRepeater && boardRolesArray.length === 0 && minRoles > 0) {
    const initialRoles = Array.from({ length: minRoles }, () => ({
      rep_company_name: "",
      rep_board_title: "",
      rep_start_date: "",
      rep_end_date: "",
    }));
    setValue('board_roles', initialRoles, { shouldValidate: false });
    boardRolesArray = initialRoles; // Update local variable immediately
  }

  // Register board_roles field with validation
  const checkRequired = (field, value) => {
    if (field?.is_required === 1 && (!value || value.trim() === "")) {
      const requiredValidation = field?.validation?.find(v => v.key === "required");
      return requiredValidation?.message || `${field?.label} is required`;
    }
    return null;
  };

  const validateDate = (role) => {
    if (!role?.is_currently_working && role.rep_start_date && role.rep_end_date) {
      // const startDate = new Date(role.rep_start_date);
      // const endDate = new Date(role.rep_end_date);

      const startDate = new Date(`${normalizeMonthYear(role.rep_start_date)}-01`);

      const endDate = new Date(`${normalizeMonthYear(role.rep_end_date)}-01`);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }
    return null;
  };

  const validateRole = (role) => {
    const error =
      checkRequired(companyNameField, role.rep_company_name) ||
      checkRequired(boardTitleField, role.rep_board_title) ||
      checkRequired(startDateField, role.rep_start_date);

    if (error) return error;

    if (!role?.is_currently_working) {
      const endDateError = checkRequired(endDateField, role.rep_end_date);
      if (endDateError) return endDateError;
    }

    return validateDate(role);
  };

  React.useEffect(() => {
    if (!shouldShowRepeater || !register) return;

    // Capture current field definitions in local variables to avoid stale closure
    const _companyNameField = companyNameField;
    const _boardTitleField = boardTitleField;
    const _startDateField = startDateField;
    const _endDateField = endDateField;
    const _minRoles = minRoles;
    const _minValidationMessage = minValidationMessage;

    const _checkRequired = (field, value) => {
      if (field?.is_required === 1 && (!value || value.trim() === "")) {
        const requiredValidation = field?.validation?.find(v => v.key === "required");
        return requiredValidation?.message || `${field?.label} is required`;
      }
      return null;
    };

    const _validateDate = (role) => {
      if (!role?.is_currently_working && role.rep_start_date && role.rep_end_date) {
        const startDate = new Date(role.rep_start_date);
        const endDate = new Date(role.rep_end_date);
        if (startDate > endDate) {
          return "Start date must be earlier than or equal to end date";
        }
      }
      return null;
    };

    const _validateRole = (role) => {
      const error =
        _checkRequired(_companyNameField, role.rep_company_name) ||
        _checkRequired(_boardTitleField, role.rep_board_title) ||
        _checkRequired(_startDateField, role.rep_start_date);

      if (error) return error;

      if (!role?.is_currently_working) {
        const endDateError = _checkRequired(_endDateField, role.rep_end_date);
        if (endDateError) return endDateError;
      }

      return _validateDate(role);
    };

    register("board_roles", {
      validate: (value) => {
        // value may be a JSON string (from hidden input) or the array directly
        let roles;
        try {
          roles = typeof value === "string" ? JSON.parse(value) : (value || []);
        } catch {
          roles = [];
        }

        // If any sub-field is required, at least one role entry is required
        const anySubFieldRequired =
          _companyNameField?.is_required === 1 ||
          _boardTitleField?.is_required === 1 ||
          _startDateField?.is_required === 1 ||
          _endDateField?.is_required === 1;

        if (anySubFieldRequired && roles.length === 0) {
          const requiredMsg =
            _companyNameField?.validation?.find(v => v.key === "required")?.message ||
            "At least one board role is required.";
          return requiredMsg;
        }

        if (_minRoles > 0 && roles.length < _minRoles) {
          return _minValidationMessage || `At least ${_minRoles} board role(s) required`;
        }

        for (const role of roles) {
          const roleError = _validateRole(role);

          if (roleError) {
            return roleError; // Block submission
          }
        }

        return true;
      }
    });

  }, [
    shouldShowRepeater,
    minRoles,
    minValidationMessage,
    companyNameField,
    boardTitleField,
    startDateField,
    endDateField,
    register
  ]);

  // If any of these fields don't exist, don't render
  if (!companyNameField || !boardTitleField || !startDateField) {
    return null;
  }

  // Check if user can add more roles (current count < max)
  const canAddMore = boardRolesArray.length < maxRoles;

  // Add a new empty board role group
  const handleAddRole = () => {
    if (canAddMore) {
      const newRole = {
        rep_company_name: "",
        rep_board_title: "",
        rep_start_date: "",
        rep_end_date: "",
      };
      const updatedRoles = [...boardRolesArray, newRole];
      setValue('board_roles', updatedRoles);
      // Don't clear errors here - let validation run naturally
    }
  };

  // Remove a specific board role group by index
  const handleRemoveRole = (index) => {
    const updatedRoles = boardRolesArray.filter((_, i) => i !== index);
    setValue('board_roles', updatedRoles);
    trigger('board_roles');
  };

  // Update a specific field within a board role group
  const handleFieldChange = (index, fieldName, value) => {
    const updatedRoles = [...boardRolesArray];
    updatedRoles[index] = {
      ...updatedRoles[index],
      [fieldName]: value,
    };

    // Special handling for "Currently Working" checkbox - ONLY ONE can be checked
    if (fieldName === 'is_currently_working') {
      if (value === true) {
        // If checking this checkbox, uncheck all others
        updatedRoles.forEach((role, i) => {
          if (i !== index) {
            role.is_currently_working = false;
          }
        });
        // Clear the end date for the currently working role
        updatedRoles[index].rep_end_date = "";
      }
      // If unchecking, just uncheck it (no need to uncheck others)
    }

    // Special handling for end date field - NO AUTO-CHECK
    // Don't auto-check "Currently Working" when end date is cleared
    // User must explicitly check the checkbox

    setValue('board_roles', updatedRoles);
    // Trigger validation after change to update errors
    trigger('board_roles');
  };

  // Validate individual field based on field definition from API
  const validateField = (index, fieldName, fieldDef) => {
    const role = boardRolesArray[index];
    const value = role?.[fieldName];

    const requiredError = checkRequiredValidation(value, fieldDef);
    if (requiredError) return requiredError;

    const validationError = checkFieldValidations(value, fieldDef);
    if (validationError) return validationError;

    return null;
  };

  const checkRequiredValidation = (value, fieldDef) => {
    if (fieldDef?.is_required !== 1) return null;

    if (!value || value.trim() === "") {
      const requiredValidation = fieldDef?.validation?.find(
        (v) => v.key === "required"
      );
      return requiredValidation?.message || `${fieldDef?.label} is required`;
    }

    return null;
  };

  const checkFieldValidations = (value, fieldDef) => {
    // ✅ VERY IMPORTANT FIX
    if (!value || value.trim() === "") return null;

    if (!fieldDef?.validation) return null;

    for (const validation of fieldDef.validation) {
      const error =
        validateMin(value, validation) ||
        validateMax(value, validation) ||
        validatePattern(value, validation);

      if (error) return error;
    }

    return null;
  };

  const validateMin = (value, validation) => {
    if (validation.key === "minlength" || validation.key === "min") {
      if (value.length < validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validateMax = (value, validation) => {
    if (validation.key === "maxlength" || validation.key === "max") {
      if (value.length > validation.length) {
        return validation.message;
      }
    }
    return null;
  };

  const validatePattern = (value, validation) => {
    if (validation.key === "pattern" && validation.regex) {
      const regex = new RegExp(validation.regex.slice(1, -1));
      if (!regex.test(value)) {
        return validation.message;
      }
    }
    return null;
  };

  // Validate all board roles before submission
  // Validate date range: rep_start_date must be <= rep_end_date
  // Start Date is required, End Date is optional (unless not currently working)
  const validateDateRange = (index) => {
    const role = boardRolesArray[index];

    // If currently working, end date is not required and validation is skipped
    if (role?.is_currently_working) {
      return null;
    }

    // If not currently working, validate date range
    if (role?.rep_start_date && role?.rep_end_date) {
      const startDate = new Date(role.rep_start_date);
      const endDate = new Date(role.rep_end_date);

      if (startDate > endDate) {
        return "Start date must be earlier than or equal to end date";
      }
    }

    // If only end date is provided without start date, that's an error
    if (!role?.rep_start_date && role?.rep_end_date) {
      return "Start date is required when end date is provided";
    }

    return null;
  };

  if (!shouldShowRepeater) {
    return null;
  }

  // Only show validation errors if form has been submitted
  const showValidationErrors = formState?.isSubmitted;
  const isBoardRoleRequired =
    companyNameField?.is_required === 1 ||
    boardTitleField?.is_required === 1 ||
    startDateField?.is_required === 1 ||
    endDateField?.is_required === 1;
  // const hasEmptyRole = boardRolesArray.every(role =>
  //   !role.rep_company_name &&
  //   !role.rep_board_title &&
  //   !role.rep_start_date &&
  //   !role.rep_end_date
  // );

  const hasEmptyRole =
    Array.isArray(boardRolesArray) &&
    boardRolesArray.every(role =>
      !role?.rep_company_name &&
      !role?.rep_board_title &&
      !role?.rep_start_date &&
      !role?.rep_end_date
    );

  return (
    <>
      {/* Hidden input so RHF tracks board_roles in its field map and runs validate on submit */}
      <input
        type="hidden"
        {...register("board_roles")}
        value={JSON.stringify(boardRolesArray)}
        readOnly
      />
      {/* Render each board role group inline */}
      {boardRolesArray.map((role, index) => {

        // Validation errors
        const companyError = showValidationErrors
          ? validateField(index, "rep_company_name", companyNameField)
          : null;

        const titleError = showValidationErrors
          ? validateField(index, "rep_board_title", boardTitleField)
          : null;

        const startDateError = showValidationErrors
          ? validateField(index, "rep_start_date", startDateField)
          : null;

        const dateRangeError = showValidationErrors
          ? validateDateRange(index)
          : null;

        // End date validation logic
        const isEndDateRequired =
          showValidationErrors &&
          !role?.is_currently_working &&
          endDateField?.is_required === 1;

        const isEndDateEmpty =
          !role?.rep_end_date || role?.rep_end_date === "";

        const showEndDateError = isEndDateRequired && isEndDateEmpty;

        const endDateErrorMessage =
          endDateField?.validation?.find(v => v.key === "required")?.message ||
          `${endDateField?.label} is required`;

        // Disable checkbox if another role is currently working
        const disableCurrentlyWorking =
          boardRolesArray.some((r, i) => i !== index && r.is_currently_working);

        return (
          <Col md={12} key={`board_role_${role.id}`} className="mb-4">
            {index === 0 && (
              <>
                {/* <Form.Label>{pastBoardRolesField?.label}</Form.Label> */}
                {showValidationErrors && isBoardRoleRequired && hasEmptyRole && (
                  <div style={{ color: "#dc3545", fontSize: "0.875em" }}>
                    Add at least one board role
                  </div>
                )}
              </>
            )}
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: "8px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
              }}
            >
              <div className="row">

                {/* Company Name */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {companyNameField?.label} {companyNameField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={companyNameField?.placeholder}
                    maxLength={255}
                    value={role?.rep_company_name || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_company_name", e.target.value)
                    }
                    className={companyError ? "is-invalid" : ""}
                  />
                  {companyError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {companyError}
                    </span>
                  )}
                </Col>

                {/* Board Title */}
                <Col md={12} className="mb-3">
                  <Form.Label className="form-label">
                    {boardTitleField?.label} {boardTitleField?.is_required ? "*" : ""}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={boardTitleField?.placeholder}
                    maxLength={255}
                    value={role?.rep_board_title || ""}
                    onChange={(e) =>
                      handleFieldChange(index, "rep_board_title", e.target.value)
                    }
                    className={titleError ? "is-invalid" : ""}
                  />
                  {titleError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {titleError}
                    </span>
                  )}
                </Col>

                {/* Start Date */}
                <Col md={6} className="mb-3">
                  <Form.Label className="form-label">
                    {startDateField?.label} {startDateField?.is_required ? "*" : ""}
                  </Form.Label>
                  <div className="w-100 react-datepicker-wrapper-custom position-relative">
                    <DatePicker
                      // selected={role?.rep_start_date ? new Date(role.rep_start_date + "-01T00:00:00") : null}
                      selected={
                        role?.rep_start_date
                          ? new Date(
                            `${normalizeMonthYear(role.rep_start_date)}-01T00:00:00`
                          )
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          handleFieldChange(index, "rep_start_date", `${year}-${month}`);
                        } else {
                          handleFieldChange(index, "rep_start_date", "");
                        }
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      onKeyDown={(e) => e.preventDefault()}
                      placeholderText={startDateField?.placeholder || "Select Month and Year"}
                      className={`form-control ${startDateError ? "is-invalid" : ""}`}
                    />
                    <FaRegCalendarAlt
                      className="position-absolute"
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none"
                      }}
                    />

                  </div>
                  {startDateError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {startDateError}
                    </span>
                  )}
                </Col>

                {/* End Date */}
                <Col md={6} className="mb-3">
                  <Form.Label className="form-label">
                    {endDateField?.label} {!role?.is_currently_working && endDateField?.is_required ? "*" : ""}
                  </Form.Label>

                  <div className="w-100 react-datepicker-wrapper-custom position-relative">
                    <DatePicker
                      // selected={role?.rep_end_date ? new Date(role.rep_end_date + "-01T00:00:00") : null}
                      selected={
                        role?.rep_end_date
                          ? new Date(
                            `${normalizeMonthYear(role.rep_end_date)}-01T00:00:00`
                          )
                          : null
                      }
                      onChange={(date) => {
                        if (date) {
                          const year = date.getFullYear();
                          const month = String(date.getMonth() + 1).padStart(2, '0');
                          handleFieldChange(index, "rep_end_date", `${year}-${month}`);
                        } else {
                          handleFieldChange(index, "rep_end_date", "");
                        }
                      }}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      onKeyDown={(e) => e.preventDefault()}
                      placeholderText={endDateField?.placeholder || "Select Month and Year"}
                      className={`form-control ${showEndDateError ? "is-invalid" : ""}`}
                      disabled={role?.is_currently_working}
                    />
                    <FaRegCalendarAlt
                      className="position-absolute"
                      style={{
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        pointerEvents: "none"
                      }}
                    />
                  </div>

                  {showEndDateError && (
                    <span
                      role="alert"
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {endDateErrorMessage}
                    </span>
                  )}

                  {dateRangeError && (
                    <span
                      style={{
                        color: "#dc3545",
                        fontSize: "0.875em",
                        margin: "4px 0px 8px",
                        display: "block",
                      }}
                    >
                      {dateRangeError}
                    </span>
                  )}
                </Col>

                {/* Checkbox + Remove */}
                <Col md={12}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Form.Check
                      type="checkbox"
                      id={`currently_working_${index}`}
                      label="Currently in this role"
                      checked={role?.is_currently_working || false}
                      onChange={(e) =>
                        handleFieldChange(index, "is_currently_working", e.target.checked)
                      }
                      disabled={disableCurrentlyWorking}
                    />

                    {boardRolesArray.length > minRoles && (
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveRole(index)}
                        title="Remove this board role"
                      >
                        -
                      </button>
                    )}
                  </div>
                </Col>

              </div>
            </div>
          </Col>
        );
      })}

      {/* Minimum count validation message - only show if form was submitted */}
      {showValidationErrors && minRoles > 0 && boardRolesArray.length > 0 && boardRolesArray.length < minRoles && (
        <Col md={12} className="mb-3">
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
            }}
          >
            {minValidationMessage || `At least ${minRoles} board role(s) required`}
          </span>
        </Col>
      )}

      {/* Add More Button - shown only if max not reached, placed after last end_date */}
      {/* Show add button if: current count < max AND we have at least min groups */}
      {canAddMore && boardRolesArray.length >= minRoles && (
        <Col md={12} className="mb-3">
          <button
            type="button"
            className="btn loginsubmit btn-red"
            onClick={handleAddRole}
            title={`Add another board role (${boardRolesArray.length}/${maxRoles})`}
          >
            + Add Board Role
          </button>
        </Col>
      )}
    </>
  );
};

BoardRolesRepeater.propTypes = {
  allFields: PropTypes.shape({
    find: PropTypes.func
  }).isRequired,
  deps: PropTypes.shape({
    errors: PropTypes.shape({
      board_roles: PropTypes.string
    }).isRequired,
    watch: PropTypes.func,
    setValue: PropTypes.func,
    trigger: PropTypes.func,
    register: PropTypes.string,
    setting: PropTypes.shape({
      board_roles_validation: PropTypes.string
    }).isRequired,
    formState: PropTypes.shape({
      isSubmitted: PropTypes.string
    }).isRequired,

  }).isRequired
}

// Export wrapper that properly instantiates the React component
export const renderBoardRolesInline = (allFields, deps) => {
  return <BoardRolesRepeater allFields={allFields} deps={deps} />;
};

// Legacy field renderer for backward compatibility
const renderPastBoardRolesField = (field, deps) => {
  const { control, errors, setting, settingData } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  // Parse board_roles_validation from Redux settings
  // Extract min and max values, ignoring the type field
  // Use settingData if available, otherwise use setting from Redux
  const effectiveSetting = settingData?.data || setting;

  if (effectiveSetting?.board_roles_validation) {
    try {
      const validationData = typeof effectiveSetting.board_roles_validation === 'string'
        ? JSON.parse(effectiveSetting.board_roles_validation)
        : effectiveSetting.board_roles_validation;
      if (Array.isArray(validationData)) {
        // Find the min entry (ignore type field)
      }
    } catch (error) {
      console.error('Error parsing board_roles_validation:', error);
    }
  }

  return status ? (
    <Col md={12} className="mb-3">
      <Form.Group>
        <Form.Label>{field.label}</Form.Label>
        <Controller
          name={reg_key}
          control={control}
          defaultValue={[]}
          rules={{
            ...buildValidationRules(validationRule, is_required),

            required: "Current / Past Board Roles minimum 1 and maximum 3 roles are required.",

            validate: (value) => {
              if (!value || value.length === 0) {
                return "Current / Past Board Roles minimum 1 and maximum 3 roles are required.";
              }

              if (value.length > 3) {
                return "Current / Past Board Roles minimum 1 and maximum 3 roles are required.";
              }

              return true;
            }
          }}
          render={({ field }) => (
            <CreatableSelect
              {...field}
              isMulti
              options={[]}
              placeholder={placeholder}
              className="basic-multi-select"
              classNamePrefix="select"
              formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
              onChange={(selectedOptions) => {
                const selectedValues = selectedOptions
                  ? selectedOptions.map((opt) => opt.value || opt.label)
                  : [];
                field.onChange(selectedValues);
              }}
              value={
                Array.isArray(field.value)
                  ? field.value.map(val => ({ value: val, label: val }))
                  : []
              }
              components={{
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
                Menu: () => null,
              }}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '38px',
                  border: '1px solid #ced4da',
                  borderRadius: '0.375rem',
                  '&:hover': {
                    borderColor: '#ced4da'
                  }
                }),
                multiValue: (base) => ({
                  ...base,
                  backgroundColor: '#e9ecef',
                  borderRadius: '0.25rem'
                }),
                multiValueLabel: (base) => ({
                  ...base,
                  color: '#495057'
                }),
                placeholder: (base) => ({
                  ...base,
                  color: '#6c757d'
                })
              }}
            />
          )}
        />
        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderSingleSelectField = (field, deps) => {
  const { reg_key } = field;

  // role_id is not rendered in update profile
  if (reg_key === "role_id") {
    return null;
  }

  switch (reg_key) {
    case "programmes":
      return renderProgramSelect(field, deps);
    case "program_study_id":
      return renderProgramStudySelect(field, deps);
    case "university_partners_id":
      return renderUniversityPartnerSelect(field, deps);
    case "year_of_graduation":
      return renderYearSelect(field, deps);
    case "experience_levels":
      return renderExperienceSelect(field, deps);
    case "country":
      return renderCountrySelect(field, deps);
    case "language":
      return renderLanguageSelect(field, deps);
    case "industry_id":
      return renderIndustrySelect(field, deps);
    case "number_of_mantees":
      return renderMenteeCountSelect(field, deps);
    case "any_particular_program":
      return renderProgramInterestSelect(field, deps);
    // Board-related SingleSelect fields only
    case "years_of_board_experience":
      return renderYearsOfBoardExperienceSelect(field, deps);
    case "accredited_director":
      return renderAccreditedDirectorSelect(field, deps);
    case "indicate_board_experience_level":
      return renderBoardExperienceLevelSelect(field, deps);
    default:
      return null;
  }
};



const renderEmployeeRadioField = ({
  reg_key,
  label,
  placeholder,
  validationRule,
  is_required,
  employee,
  register,
  setOrganizationPlaceholder,
  errors,
}) => {
  const [yesLabel, noLabel] = placeholder?.split("|") || [];

  return (
    <Col sm={12} className="mb-3">
      <div className="d-flex flex-column">
        <label className="form-label">{label}</label>

        <div className="d-flex">
          <span className="custom_radio ms-0">
            {yesLabel}&nbsp;
            <input
              type="radio"
              name={reg_key}
              value={yesLabel}
              checked={employee?.toLowerCase() === "yes"}
              {...register(reg_key, {
                ...buildValidationRules(validationRule, is_required),
              })}
              onChange={setOrganizationPlaceholder}
            />
            <span className="radio_indicator">&nbsp;</span>
          </span>

          <span className="custom_radio">
            {noLabel}&nbsp;
            <input
              type="radio"
              name={reg_key}
              value={noLabel}
              checked={employee?.toLowerCase() === "no"}
              {...register(reg_key, {
                ...buildValidationRules(validationRule, is_required),
              })}
              onChange={setOrganizationPlaceholder}
            />
            <span className="radio_indicator">&nbsp;</span>
          </span>
        </div>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{ color: "#dc3545", fontSize: "0.875em", margin: "4px 0px 8px" }}
          >
            {errors[reg_key].message}
          </span>
        )}
      </div>
    </Col>
  );
};
const renderGenderRadioField = ({
  reg_key,
  label,
  placeholder,
  validationRule,
  is_required,
  gender,
  register,
  handleGender,
  errors,
}) => {
  const [maleLabel, femaleLabel] = placeholder?.split("|") || [];

  return (
    <Col md={12} className="mb-3">
      <div className="d-flex flex-column">
        <label className="form-label">{label}</label>
        <div className="d-flex">
          <span className="custom_radio ms-0">
            {maleLabel}&nbsp;
            <input
              type="radio"
              name={reg_key}
              value={maleLabel}
              checked={gender?.toLowerCase() === "male"}
              {...register(reg_key, {
                ...buildValidationRules(validationRule, is_required),
              })}
              onClick={(e) => handleGender(e.target.value)}
            />
            <span className="radio_indicator">&nbsp;</span>
          </span>

          <span className="custom_radio">
            {femaleLabel}&nbsp;
            <input
              type="radio"
              name={reg_key}
              value={femaleLabel}
              checked={gender?.toLowerCase() === "female"}
              {...register(reg_key, {
                ...buildValidationRules(validationRule, is_required),
              })}
              onClick={(e) => handleGender(e.target.value)}
            />
            <span className="radio_indicator">&nbsp;</span>
          </span>
        </div>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{ color: "#dc3545", fontSize: "0.875em", margin: "4px 0px 8px" }}
          >
            {errors[reg_key].message}
          </span>
        )}
      </div>
    </Col>
  );
};

export const renderRadioField = (field, deps) => {
  const {
    register,
    errors,
    employee,
    setOrganizationPlaceholder,
    gender,
    handleGender,
  } = deps;

  const { reg_key, label, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  if (!status) return null;

  if (reg_key === "employee") {
    return renderEmployeeRadioField({
      reg_key,
      label,
      placeholder,
      validationRule,
      is_required,
      employee,
      register,
      setOrganizationPlaceholder,
      errors,
    });
  }

  if (reg_key === "gender") {
    return renderGenderRadioField({
      reg_key,
      label,
      placeholder,
      validationRule,
      is_required,
      gender,
      register,
      handleGender,
      errors,
    });
  }

  return null;
};

const SimpleCheckbox = ({
  reg_key,
  label,
  register,
  validationRule,
  is_required,
  errors,
}) => (
  <Col md="12" className="mb-3">
    <span className="custom_check me-2">
      {label}
      <input
        type="checkbox"
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
      />
      <span className="check_indicator">&nbsp;</span>
    </span>

    {errors?.[reg_key] && (
      <span
        role="alert"
        style={{ color: "#dc3545", fontSize: "0.875em", margin: "4px 0px 8px" }}
      >
        {errors[reg_key].message}
      </span>
    )}
  </Col>
);

const TermsandConditionsCheckbox = ({
  reg_key,
  label,
  register,
  validationRule,
  is_required,
  errors,
}) => {
  return (
    <Col md={12} className="mb-3">
      <div className="form-group termsconditions mb-0 d-flex">

        <span className="custom_check m-0">
          <input
            type="checkbox"
            className="form-check-input"
            {...register(reg_key, {
              ...buildValidationRules(
                validationRule,
                is_required
              ),
            })}
          />

          <span className="check_indicator">
            &nbsp;
          </span>
        </span>

        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </div>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

TermsandConditionsCheckbox.propTypes = {
  reg_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  validationRule: PropTypes.array,
  is_required: PropTypes.bool,
  errors: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      message: PropTypes.string.isRequired,
    }),
  }),
};

const PrivacyPolicyCheckbox = ({
  reg_key,
  label,
  register,
  validationRule,
  is_required,
  errors,
}) => {
  return (
    <Col md={12} className="mb-3">
      <div className="form-group termsconditions mb-0 d-flex">

        <span className="custom_check m-0">
          <input
            type="checkbox"
            className="form-check-input"
            {...register(reg_key, {
              ...buildValidationRules(
                validationRule,
                is_required
              ),
            })}
          />

          <span className="check_indicator">
            &nbsp;
          </span>
        </span>

        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </div>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{
            color: "#dc3545",
            fontSize: "0.875em",
            margin: "4px 0px 8px",
          }}
        >
          {errors?.[reg_key]?.message}
        </span>
      )}
    </Col>
  );
};

PrivacyPolicyCheckbox.propTypes = {
  reg_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  validationRule: PropTypes.array,
  is_required: PropTypes.bool,
  errors: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      message: PropTypes.string.isRequired,
    }),
  }),
};

const OpenToTalkCheckbox = ({
  reg_key,
  label,
  register,
  validationRule,
  is_required,
  errors,
}) => (

  <Col md="12" className="mb-3">
    {/* Static heading */}
    <label className="form-label" htmlFor={reg_key}>
      Indicate your preference for below
    </label>

    {/* Checkbox with dynamic label */}
    <span className="custom_check me-2 d-flex align-items-center">
      <input
        type="checkbox"
        id={reg_key}
        name={reg_key}
        {...register(reg_key, {
          ...buildValidationRules(validationRule, is_required),
        })}
      />
      <span className="check_indicator">&nbsp;</span>

      {/* API text */}
      <span className="">{label}</span>
    </span>

    {errors?.[reg_key] && (
      <span
        role="alert"
        style={{
          color: "#dc3545",
          fontSize: "0.875em",
          margin: "4px 0px 8px",
          display: "block",
        }}
      >
        {errors[reg_key].message}
      </span>
    )}
  </Col>
);

OpenToTalkCheckbox.propTypes = {
  reg_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  validationRule: PropTypes.array,
  is_required: PropTypes.bool,
  errors: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      message: PropTypes.string.isRequired,
    }),
  }),
};


const MentalHealthCheckbox = ({
  reg_key,
  label,
  register,
  watch,
  setCheckBox,
  setValue,
  clearErrors,
  trigger,
  validationRule,
  is_required,
  errors,
}) => {
  const value = watch(reg_key);

  return (
    <Col md="12" className="mb-3">
      <label className="form-label" htmlFor="perference level">Indicate your preference for below</label>

      <span className="custom_check me-2">
        {label}
        <input
          type="checkbox"
          checked={value || false}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
          })}
          onChange={(e) => {
            const checked = e.target.checked;
            setCheckBox(checked);
            setValue(reg_key, checked);
            clearErrors(reg_key);
            if (!checked && is_required) trigger(reg_key);
          }}
        />
        <span className="check_indicator">&nbsp;</span>
      </span>

      {errors?.[reg_key] && (
        <span
          role="alert"
          style={{ color: "#dc3545", fontSize: "0.875em" }}
        >
          {errors[reg_key].message}
        </span>
      )}
    </Col>
  );
};


const toggleExperienceValue = (
  currentValue,
  id,
  field,
  setExperience,
  clearErrors,
  reg_key
) => {
  const newValues = currentValue.includes(id)
    ? currentValue.filter(item => item !== id)
    : [...currentValue, id];

  field.onChange(newValues);
  setExperience(newValues);

  if (newValues.length > 0) {
    clearErrors(reg_key);
  }
};

export const renderCheckboxField = (field, deps) => {
  const {
    register,
    errors,
    control,
    clearErrors,
    setValue,
    trigger,
    watch,
    setCheckBox,
    userExperience,
    setExperience,
  } = deps;

  const { reg_key, label, validation, status, is_required } = field;
  const validationRule = validation;

  if (!status) return null;

  // Handle experience_levels as multiple checkboxes
  if (reg_key === "experience_levels") {
    return (
      <Col md="12" className="mb-3">
        <label className="form-label" htmlFor="experience_levels">{label}</label>
        <Controller
          name={reg_key}
          control={control}
          rules={{
            ...buildValidationRules(validationRule, is_required, reg_key)
          }}
          render={({ field }) => {
            const currentValue = Array.isArray(field.value) ? field.value : [];

            return (
              <>
                {userExperience?.map((val) => (
                  <span key={val.id} className="custom_check me-2">
                    {val?.title}
                    <input
                      type="checkbox"
                      checked={currentValue.includes(val.id)}
                      onChange={() =>
                        toggleExperienceValue(
                          currentValue,
                          val.id,
                          field,
                          setExperience,
                          clearErrors,
                          reg_key
                        )
                      }
                    />
                    <span className="check_indicator">&nbsp;</span>
                  </span>
                ))}
                {/* All of above */}
                <span className="custom_check me-2">
                  All of above{" "}
                  <input
                    type="checkbox"
                    checked={
                      userExperience?.length > 0 &&
                      currentValue.length === userExperience?.length
                    }
                    onChange={(e) => {
                      if (e.target.checked) {
                        const allIds = userExperience.map(exp => exp.id);
                        field.onChange(allIds);
                        setExperience(allIds);
                      } else {
                        field.onChange([]);
                        setExperience([]);
                      }
                      clearErrors(reg_key);
                    }}
                  />
                  <span className="check_indicator">&nbsp;</span>
                </span>
              </>
            );
          }}
        />
        <div>
          {errors?.[reg_key] && (
            <span role="alert" style={{
              color: '#dc3545',
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}>
              {errors?.[reg_key]?.message}
            </span>
          )}
        </div>
      </Col>
    );
  }

  const renderers = {
    mn_open_to_talk: () =>
      OpenToTalkCheckbox({
        reg_key,
        label,
        register,
        validationRule,
        is_required,
        errors,
      }),

    mn_loking_for_coach: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    lgbtq_discussion: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    mf_mental_health: () =>
      MentalHealthCheckbox({
        reg_key,
        label,
        register,
        watch,
        setCheckBox,
        setValue,
        clearErrors,
        trigger,
        validationRule,
        is_required,
        errors,
      }),

    mf_certified_coach: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    is_terms: () =>
      TermsandConditionsCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    is_privacy: () =>
      PrivacyPolicyCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    my_consent: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    is_disclaimer: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),

    training_video: () =>
      SimpleCheckbox({ reg_key, label, register, validationRule, is_required, errors }),
  };

  return renderers[reg_key]?.() || null;
};


export const renderDatePickerField = (field, deps) => {
  const {
    register,
    errors,
    setValue,
    watch,
    available,
    showDateDiv,
    checkFromDate,
    checkToDate,
    fromDate,
    toDate,
    dob,
    setDob,
    clearErrors,
    showDatePicker,
  } = deps;
  const { reg_key, label, validation, status, is_required } = field;
  const validationRule = validation;

  if (reg_key === "availability") {
    return status ? (
      <>
        {/* Checkbox for availability */}
        <Col md="12" className="unavailable-mentor">
          <span className="custom_check me-2 mb-2">
            {label}
            <input
              type="checkbox"
              name="availability"
              checked={available === 2}
              value={available}
              {...register("availability")}
              onChange={showDatePicker}
            />
            <span className="check_indicator">&nbsp;</span>
          </span>
        </Col>

        {/* From and To Date Fields */}
        <div className="d-flex mb-3 gap-2">
          {/* From Date */}
          <Col md={6} style={{ display: showDateDiv }}>
            <Form.Group controlId="fromDate">
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                name="from_date"
                {...register("from_date", {
                  required: available === 2 ? "From date is required when unavailable" : false,
                  validate: (value) => {
                    const to = watch("to_date");
                    if (!value) return true;
                    if (to && new Date(value) > new Date(to)) {
                      return "From date must be earlier than or equal to To date";
                    }
                    return true;
                  },
                })}
                value={fromDate || ""}
                onChange={(e) => {
                  checkFromDate(e.target.value);
                  setValue("from_date", e.target.value, { shouldValidate: true });
                }}
              />
              {errors?.from_date && (
                <label className="customeValidationText text-left text-danger">
                  {errors.from_date.message}
                </label>
              )}
            </Form.Group>
          </Col>

          {/* To Date */}
          <Col md={6} style={{ display: showDateDiv }}>
            <Form.Group controlId="toDate">
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                name="to_date"
                {...register("to_date", {
                  required: available === 2
                    ? "To date is required when unavailable"
                    : false,
                })}
                value={toDate || ""}
                onChange={(e) => {
                  checkToDate(e.target.value);
                  setValue("to_date", e.target.value, { shouldValidate: true });
                }}
              />
              {errors?.to_date && (
                <label className="customeValidationText text-left text-danger">
                  {errors.to_date.message}
                </label>
              )}
            </Form.Group>
          </Col>
        </div>
      </>
    ) : null;
  }

  // General Date Field (like DOB)
  return status ? (
    <Col md={12} className="asAFormField mb-3">
      <Form.Group controlId={reg_key}>
        <Form.Label>{label}</Form.Label>

        <Form.Control
          type="date"
          name={reg_key}
          value={dob || ""}
          max={getMaxDob()}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: (value) => validateDob(value, is_required),
          })}
          onChange={(e) => {
            setDob(e.target.value);
            setValue(reg_key, e.target.value);
            clearErrors(reg_key);
          }}
        />

        {errors?.[reg_key] && (
          <span
            style={{ color: "#dc3545", fontSize: "0.875em", marginTop: "4px" }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </Form.Group>
    </Col>
  ) : null;
};

export const renderFileField = (field, deps) => {
  const {
    control,
    errors,
    src,
    alt,
    selectedFile,
    onChangePicture,
    resumeText,
    onChangeResume,
    clearErrors,
    placeholder,
    userData,
    selectedResume,
    setResumeText,
    setSelectedResume,
    setValue,
    dispatch
  } = deps;
  const { reg_key, validation, status, is_required } = field;
  const validationRule = validation;

  const handleDownloadResume = (e) => {
    e.preventDefault();
    // If userData has resume URL, open it in a new tab
    if (userData?.resume) {
      window.open(userData.resume, '_blank');
    } else if (selectedResume instanceof File) {
      // If selectedResume is a newly selected file, create a blob URL and open in new tab
      const blobUrl = URL.createObjectURL(selectedResume);
      window.open(blobUrl, '_blank');
      // Clean up blob URL after a delay (after tab opens)
      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } else {
      console.warn('No resume available to preview');
    }
  };

  const handleDeleteResume = async () => {
    try {
      const res = await dispatch(
        deleteResume(userData?.id)
      ).unwrap();
      // clear local states
      setResumeText("");
      // clear selected resume
      setSelectedResume("");
      // clear form value
      setValue("resume", null);
      // clear validation
      clearErrors("resume");
      renderGlobalModal({ message: res.message });
    } catch (err) {
      console.error("Delete resume failed", err);
    }
  };

  if (reg_key === "image") {
    return status ? (
      <Col md={12} className="asAFormField mb-3">
        <div className="userprofile text-center">
          <label htmlFor="profileImage" className="customeValidationImageText text-center form-label">
            Image size should be less than 30 MB*
          </label>

          <span>
            <img
              className="img-fluid rounded-circle userprofile-img"
              src={src || placeholder}
              alt={alt || "Upload an Image"}
            />
          </span>

          <div>
            <span className="inputfileouter">
              Upload image{" "}
              <Controller
                name={reg_key}
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value && !selectedFile && !userData?.image && is_required) {
                      return "Profile image is required";
                    }
                    return true;
                  },
                }}
                render={({ field: { onChange, value, ...field } }) => (
                  <input
                    {...field}
                    type="file"
                    accept=".png, .jpg, .jpeg"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      onChange(file);
                      onChangePicture(e);
                    }}
                    className="form-control"
                  />
                )}
              />
            </span>
          </div>

          {errors?.[reg_key] && (
            <span
              role="alert"
              style={{
                color: "#dc3545",
                fontSize: "0.875em",
                margin: "4px 0px 8px",
              }}
            >
              {errors?.[reg_key]?.message}
            </span>
          )}
        </div>
      </Col>
    ) : null;
  }

  if (reg_key === "resume") {
    return status ? (
      <Col md={12} className="mb-3">
        <Form.Label>{field.label}</Form.Label>

        <div className="form-group inputfileupload mb-0">
          <input
            type="text"
            disabled
            className="form-control uploadInput"
            value={resumeText || field.placeholder}
          />

          <span
            className="uploadfile uploadfile1"
            style={{ top: "10px" }}
          >
            <Controller
              name={reg_key}
              control={control}
              rules={{
                ...((selectedResume === "" ||
                  typeof selectedResume !== "string") &&
                  buildValidationRules(
                    validationRule,
                    is_required,
                    reg_key
                  )),
              }}
              render={({ field, fieldState: { error } }) => {
                const resumeInputKey = selectedResume && selectedResume.name
                  ? `${selectedResume.name}-${selectedResume.lastModified}`
                  : `resume-input-${resumeText || "empty"}`;

                return (
                  <input
                    key={resumeInputKey}
                    type="file"
                    accept=".pdf"
                    name={reg_key}
                    onChange={(e) => {
                      onChangeResume(e);
                      field.onChange(e.target.files[0]);

                      if (e.target.files[0]) {
                        clearErrors(reg_key);
                      }
                    }}
                    title=""
                  />
                );
              }}
            />
            Upload
          </span>

          <sub className="ps-2">
            Upload only PDF format.
          </sub>

          <div className="ps-2">
            {errors?.[reg_key] && (
              <span
                role="alert"
                style={{
                  color: "#dc3545",
                  fontSize: "0.875em",
                  margin: "4px 0px 8px",
                }}
              >
                {errors?.[reg_key]?.message}
              </span>
            )}
          </div>
        </div>

        {/* DOWNLOAD + DELETE OPTIONS */}
        {userData?.role_id === "Mentees" && (selectedResume || resumeText) && (
          <Row className="mt-2">
            {/* Download Resume */}
            <Col sm={6}>
              {(userData?.resume || selectedResume) && (
                <Link
                  to=""
                  className="d-block date color_red"
                  onClick={handleDownloadResume}
                  style={{ cursor: 'pointer' }}
                >
                  {"Download Resume"}
                </Link>
              )}
            </Col>

            {/* Delete Resume */}
            <Col md={6}>
              <button
                type="button"
                className="d-block date color_red border-0 bg-transparent"
                onClick={handleDeleteResume}
              >
                Delete Resume
              </button>
            </Col>
          </Row>
        )}
      </Col>
    ) : null;
  }

  return null;
};

// Helper functions for domains/areas
const getSubareaIds = (subcategory) => {
  return new Set(subcategory.map((s) => s.id.toString()));
};

const addMainAreaWithSubareas = (mainArea, selectedArea, areaData) => {
  const updatedMain = [...mainArea, areaData.id.toString()];
  const subIds = getSubareaIds(areaData.subcategory);
  const updatedSubs = Array.from(new Set([...selectedArea, ...subIds]));
  return { updatedMain, updatedSubs };
};

const removeMainAreaWithSubareas = (mainArea, selectedArea, areaData) => {
  const updatedMain = mainArea.filter((id) => id !== areaData.id.toString());
  const subIds = getSubareaIds(areaData.subcategory);
  const updatedSubs = selectedArea.filter((id) => !subIds.has(id));
  return { updatedMain, updatedSubs };
};

const addSubarea = (mainArea, selectedArea, areaData, subId) => {
  const updatedSubs = [...selectedArea, subId];
  const updatedMain = mainArea.includes(areaData.id.toString())
    ? mainArea
    : [...mainArea, areaData.id.toString()];
  return { updatedMain, updatedSubs };
};

const removeSubarea = (mainArea, selectedArea, areaData, subId) => {
  const updatedSubs = selectedArea.filter((id) => id !== subId);
  const stillHasSubareas = areaData.subcategory.some((s) =>
    updatedSubs.includes(s.id.toString())
  );
  const updatedMain = stillHasSubareas
    ? mainArea
    : mainArea.filter((id) => id !== areaData.id.toString());
  return { updatedMain, updatedSubs };
};

const handleSubareaChange = ({
  checked,
  mainArea,
  selectedArea,
  areaData,
  subId,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  const { updatedMain, updatedSubs } = checked
    ? addSubarea(mainArea, selectedArea, areaData, subId)
    : removeSubarea(mainArea, selectedArea, areaData, subId);

  setSelectedArea(updatedSubs);
  setMainArea(updatedMain);
  setValue("mainArea", updatedMain);
  setValue("subarea", updatedSubs);
  setValue("domains", updatedMain);
  field.onChange(updatedMain);
  trigger("mainArea");
};
const handleMainAreaChange = ({
  checked,
  mainArea,
  selectedArea,
  areaData,
  setMainArea,
  setSelectedArea,
  setValue,
  field,
  trigger,
}) => {
  const { updatedMain, updatedSubs } = checked
    ? addMainAreaWithSubareas(mainArea, selectedArea, areaData)
    : removeMainAreaWithSubareas(mainArea, selectedArea, areaData);

  setMainArea(updatedMain);
  setSelectedArea(updatedSubs);
  setValue("mainArea", updatedMain);
  setValue("subarea", updatedSubs);
  setValue("domains", updatedMain);
  field.onChange(updatedMain);
  trigger("mainArea");
};
const handleObjectiveChange = ({
  checked,
  value,
  selectedObjective,
  setSelectedObjective,
  setValue,
  field,
  trigger,
}) => {
  const updatedObjectives = checked
    ? [...selectedObjective, value]
    : selectedObjective.filter((val) => val !== value);

  setSelectedObjective(updatedObjectives);
  setValue("objective", updatedObjectives);
  field.onChange(updatedObjectives);
  trigger("objective");
};

const createSubareaChangeHandler =
  ({
    mainArea,
    selectedArea,
    areaData,
    subId,
    setMainArea,
    setSelectedArea,
    setValue,
    field,
    trigger,
  }) =>
    (event) => {
      handleSubareaChange({
        checked: event.target.checked,
        mainArea,
        selectedArea,
        areaData,
        subId,
        setMainArea,
        setSelectedArea,
        setValue,
        field,
        trigger,
      });
    };

// ✅ Objective Validation
const validateObjective = (value) => {
  if (!Array.isArray(value) || value.length === 0) {
    return "Please select at least one objective.";
  }

  if (value.length > 3) {
    return "You can select a maximum of 3 objectives.";
  }

  return undefined;
};

const renderDomainsField = (field, deps, getText) => {
  const {
    control,
    errors,
    setValue,
    trigger,
    mainArea,
    setMainArea,
    selectedArea,
    setSelectedArea,
    areas,
    formState,
  } = deps;

  return (
    <div
      className="profilescreen mb-3 my-3"
      style={{ width: "100%" }}
      ref={(el) => {
        if (el) {
          const hasError = !!errors?.mainArea;
          const submitCount = formState?.submitCount || 0;
          const lastSubmitCount = parseInt(el.dataset.lastSubmitCount || "0", 10);

          const isNewError = hasError && !el.dataset.hadError;
          const isNewSubmitAttempt = hasError && submitCount > lastSubmitCount;

          if (isNewError || isNewSubmitAttempt) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
          if (hasError) {
            el.dataset.hadError = "true";
          } else {
            delete el.dataset.hadError;
          }
          el.dataset.lastSubmitCount = submitCount.toString();
        }
      }}
    >
      <h4>{field.label}</h4>

      <p className="mt-2">
        {getText("mentor_domain_text", "mentees_domain_text")}
      </p>

      <div className="businessboxouter">
        <Controller
          name="mainArea"
          control={control}
          rules={{
            ...buildValidationRules(field.validation, field.is_required),
          }}
          render={({ field: ctrlField }) => (
            <>
              {errors?.mainArea && (
                <span className="text-danger d-block mb-2">
                  {errors.mainArea.message}
                </span>
              )}

              {areas?.map((areaData) => {
                const id = areaData.id.toString();
                const isSelected =
                  (ctrlField.value || []).includes(id) ||
                  mainArea.includes(id);

                return (
                  <div key={id}>
                    <h5>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        className={`form-check-input ${errors.mainArea ? "is-invalid" : ""
                          }`}
                        onChange={(e) =>
                          handleMainAreaChange({
                            checked: e.target.checked,
                            mainArea,
                            selectedArea,
                            areaData,
                            setMainArea,
                            setSelectedArea,
                            setValue,
                            field: ctrlField,
                            trigger,
                          })
                        }
                      />
                      <div className="selectboxouter">{areaData.name}</div>
                    </h5>

                    <ul className="bussinessSelect">
                      {areaData.subcategory.map((sub) => (
                        <li key={sub.id}>
                          <input
                            type="checkbox"
                            checked={selectedArea.includes(
                              sub.id.toString()
                            )}
                            className="form-check-input"
                            onChange={createSubareaChangeHandler({
                              mainArea,
                              selectedArea,
                              areaData,
                              subId: sub.id.toString(),
                              setMainArea,
                              setSelectedArea,
                              setValue,
                              field: ctrlField,
                              trigger,
                            })}
                          />
                          <div className="selectboxouter">{sub.name}</div>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </>
          )}
        />
      </div>
    </div>
  );
};

const renderObjectiveField = (field, deps, getText) => {
  const {
    control,
    errors,
    selectedObjective,
    setSelectedObjective,
    objective,
    setValue,
    trigger,
  } = deps;

  return (
    <div className="profilescreen mb-3 my-3">
      <h4>
        {getText("mentor_objective_text", "mentees_objective_text")}
      </h4>

      <div className={`businessboxouter ${errors?.objective ? "is-invalid" : ""}`}>
        <label className="form-label">{field.label}</label>

        <div className="bussinessSelect">
          <Controller
            name="objective"
            control={control}
            rules={{
              ...buildValidationRules(field.validation, field.is_required),
              validate: validateObjective,
            }}
            render={({ field: ctrlField }) => (
              <>
                {errors?.objective && (
                  <span className="text-danger d-block mb-2">
                    {errors.objective.message}
                  </span>
                )}

                {objective?.map((item) => {
                  const checked = selectedObjective.includes(
                    item.value.toString()
                  );

                  return (
                    <li key={item.value}>
                      <input
                        type="checkbox"
                        checked={checked}
                        className={`form-check-input ${errors?.objective ? "is-invalid" : ""
                          }`}
                        onChange={(e) =>
                          handleObjectiveChange({
                            checked: e.target.checked,
                            value: item.value.toString(),
                            selectedObjective,
                            setSelectedObjective,
                            setValue,
                            field: ctrlField,
                            trigger,
                          })
                        }
                      />
                      <div className="selectboxouter">{item.label}</div>
                    </li>
                  );
                })}
              </>
            )}
          />
        </div>
      </div>
    </div>
  );
};

const renderBoardSelect = (field, deps, key) => {
  const {
    control,
    errors,
    setValue,
    trigger,
    clearErrors,
    customStyles,
  } = deps;

  const options =
    deps[key]?.map((item) => ({
      value: item.id,
      label: item.title,
    })) || [];

  return (
    <Col md={12} className="mb-3">
      <Form.Group className="react-select-menu-option">
        <Form.Label>{field.label}</Form.Label>

        <Controller
          name={field.reg_key}
          control={control}
          rules={{
            ...buildValidationRules(field.validation, field.is_required),
          }}
          render={({ field: ctrlField }) => (
            <Select
              {...ctrlField}
              isMulti
              options={options}
              className="basic-multi-select"
              classNamePrefix="select"
              styles={customStyles}
              onChange={(selected) => {
                const values = selected?.map((o) => o.value) || [];
                ctrlField.onChange(values);
                setValue(field.reg_key, values.length ? values : "");
                clearErrors(field.reg_key);
                if (!values.length && field.is_required) {
                  trigger(field.reg_key);
                }
              }}
              value={options.filter((o) =>
                ctrlField.value?.includes(o.value)
              )}
            />
          )}
        />

        {errors?.[field.reg_key] && (
          <span className="text-danger">
            {errors[field.reg_key].message}
          </span>
        )}
      </Form.Group>
    </Col>
  );
};

export const renderCustomSelectField = (field, deps) => {
  const {
    roleId,
    setting,
  } = deps;

  const { reg_key, status } = field;

  if (!status) return null;

  const isMentor = roleId === "Mentor";

  const getText = (mentorKey, menteeKey) =>
    isMentor ? setting?.[mentorKey] : setting?.[menteeKey];

  // ✅ Map instead of multiple ifs
  const renderMap = {
    accredited_director: () => renderAccreditedDirectorSelect(field, deps),
    indicate_board_experience_level: () =>
      renderBoardExperienceLevelSelect(field, deps),

    domains: () =>
      renderDomainsField(field, deps, getText),

    objective: () =>
      renderObjectiveField(field, deps, getText),

    board_entity_type: () =>
      renderBoardSelect(field, deps, "boardEntityTypeExperience"),

    board_entity_type_2: () =>
      renderBoardSelect(field, deps, "boardExperienceMentor"),

    course_completed: () =>
      renderBoardSelect(field, deps, "coursesCompleted"),
  };

  return renderMap[reg_key]?.() || null;
};


export const renderTextareaField = (field, deps) => {
  const { register, errors, watch, countWords, setText } = deps;
  const { reg_key, placeholder, validation, status, is_required } = field;
  const validationRule = validation;

  return status ? (
    <Col md={12} className="mb-3">
      <div className="">
        <Form.Label>{field.label}</Form.Label>
        <Form.Control
          as="textarea"
          rows="5"
          name={reg_key}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className={"form-control"}
          {...register(reg_key, {
            ...buildValidationRules(validationRule, is_required),
            validate: {
              whiteSpace: (value) =>
                !value ||
                  (typeof value === "string" && value.trim().length > 0)
                  ? true
                  : "White spaces not allowed.",
            },
          })}
        />
        <p className="mt-2">Characters count: {countWords(watch(reg_key) || "")}/1500</p>

        {errors?.[reg_key] && (
          <span
            role="alert"
            style={{
              color: "#dc3545",
              fontSize: "0.875em",
              margin: "4px 0px 8px",
            }}
          >
            {errors?.[reg_key]?.message}
          </span>
        )}
      </div>
    </Col>
  ) : null;
};

export const renderUpdateProfileField = (field, deps) => {

  const { field_type, status } = field;
  switch (field_type) {
    case "text":
      return renderTextField(field, deps);
    case "email":
      return renderEmailField(field, deps);
    case "password":
      return renderPasswordField(field, deps);
    case "SingleSelect":
      return renderSingleSelectField(field, deps);
    case "radio":
      return renderRadioField(field, deps);
    case "checkbox":
      return renderCheckboxField(field, deps);
    case "DatePicker":
      return renderDatePickerField(field, deps);
    case "file":
      return renderFileField(field, deps);
    case "CustomSelect":
      return renderCustomSelectField(field, deps);
    case "textarea":
      return renderTextareaField(field, deps);
    default:
      return status ? (
        <Col md={12}>
          <div className="form-label">{field.label}</div>
        </Col>
      ) : null;
  }
};


OpenToTalkCheckbox.propTypes = {
  control: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  validationRule: PropTypes.any,
  is_required: PropTypes.bool,
  clearErrors: PropTypes.func.isRequired,
};
MentalHealthCheckbox.propTypes = {
  reg_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setCheckBox: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  clearErrors: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  validationRule: PropTypes.any,
  is_required: PropTypes.bool,
  errors: PropTypes.objectOf(
    PropTypes.shape({
      message: PropTypes.string,
    })
  ),
};
SimpleCheckbox.propTypes = {
  reg_key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  register: PropTypes.func.isRequired,
  validationRule: PropTypes.any,
  is_required: PropTypes.bool,
  errors: PropTypes.objectOf(
    PropTypes.shape({
      message: PropTypes.string,
    })
  ),
};
